/* eslint react-hooks/purity: ["warn"] */
import React, { useMemo } from "react";
import { useParams } from "react-router";
import { useTranslation } from "@coworker/locales";
import { useMFAQ } from "../hooks/useMFAQsService";
import VoteBox from "./VoteBoxComponent";
import TaskHistory from "./TaskHistoryComponent";
import OtherAreasComponent from "./OtherAreasComponent";
import { useStoreId } from "../../../core/auth/useLoggedInUser";
import FullscreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import {
  calculateNumberOfVotes,
  createTaskHistory,
} from "./QuestionsDetailPage.helper";
import MfaqTaskButtonComponent from "./MfaqTaskButtonComponent";
import QuestionActionButtonRowComponent from "./QuestionActionButtonRowComponent";
import { Question } from "../BackendApiRepository/QuestionResponse";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useMfaqFilters } from "../hooks/useMfaqFilters";
import { getPeriodTimestampsFromFilters } from "../helper";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import { DEFUALT_PERIOD_VALUE, PERIOD_ID } from "../hooks/usePeriodFilter";
import {
  DEFUALT_LOCATION_VALUE,
  LOCATION_ID,
} from "../hooks/useLocationFilter";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import { MfaqMergeQuestionModal } from "@coworker/app/src/components/MFAQ/QuestionDetailsPage/MfaqMergeQuestionModal";
import { MFAQ_LABEL } from "../constants";

export default function CustomerMFAQDetail() {
  const pageContainerRef = React.useRef<HTMLDivElement | null>(null);
  const store_id = useStoreId();
  const { id } = useParams();

  const mfaqFilters = useMfaqFilters({ hideQuestionsWithTasksOnly: true });

  const period = getSelectedFilterValue(
    PERIOD_ID,
    mfaqFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ).value as string;

  const location = getSelectedFilterValue(
    LOCATION_ID,
    mfaqFilters.filterValues,
    DEFUALT_LOCATION_VALUE
  ).value as string;

  const filters = useMemo(() => {
    return [period, location];
  }, [period, location]);

  const [startDate, endDate] = useMemo(() => {
    return getPeriodTimestampsFromFilters(mfaqFilters.filterValues);
  }, [mfaqFilters.filterValues]);
  const { data: question } = useMFAQ(id, startDate, endDate);

  const [questionState, setQuestionState] = React.useState<
    Question | undefined
  >(question);

  const [openMergeQuestionModal, setOpenMergeQuestionModal] =
    React.useState(false);

  const [mergeClicked, setMergeClicked] = React.useState(false);
  const [currentUpvotes, setCurrentUpvotes] = React.useState(
    question ? calculateNumberOfVotes(question, location) : 0
  );

  React.useEffect(() => {
    if (questionState) {
      setCurrentUpvotes(calculateNumberOfVotes(questionState, location));
    }
  }, [location, questionState]);

  React.useEffect(() => {
    setQuestionState(question);
  }, [question]);

  const isWholeStore = location === DEFUALT_LOCATION_VALUE.value;
  const completedTasks = question ? createTaskHistory(question, location) : [];

  const { t } = useTranslation();

  const linkbarItems = useMemo(
    () => [
      {
        label: MFAQ_LABEL,
        to: "/customerquestions",
      },
    ],
    [t]
  );

  const actions = question?.actions;
  if (questionState === undefined) {
    return <div>Loading...</div>;
  }
  const showMfaqTaskButton = !isWholeStore && !mergeClicked;

  return (
    <FullscreenPopup
      noPadding
      actionBarContent={
        showMfaqTaskButton && (
          <MfaqTaskButtonComponent filters={filters} question={questionState} />
        )
      }
      isActionBarHiddenOnScroll={false}
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={t("questionStringMFAQ")}
          currentAsH1
        />
      }
    >
      {/* why does this exist? */}
      <div ref={pageContainerRef}>
        <FilterPanel
          filterValues={mfaqFilters.filterValues}
          setFilterValues={mfaqFilters.setFilterValues}
          useFiltersFactory={mfaqFilters.useFiltersFactory}
          feature="mfaq-detail"
        />
        <VoteBox
          numberOfVotes={currentUpvotes}
          questionText={questionState?.question_text}
          isWholeStore={isWholeStore}
          questionId={id ?? ""}
          storeId={store_id}
          area={location}
          question={questionState}
          setQuestion={setQuestionState}
          endDate={endDate ?? Date.now()}
        />
        <QuestionActionButtonRowComponent
          questionId={id ?? ""}
          questionText={questionState?.question_text}
          setQuestion={setQuestionState}
          mergeQuestionButtonClicked={setMergeClicked}
          taskId={questionState?.tasks?.[0]?.task_id ?? ""}
          setOpenMergeQuestionModal={setOpenMergeQuestionModal}
        />
        <MfaqMergeQuestionModal
          questionId={id ?? ""}
          openMergeQuestionModal={openMergeQuestionModal}
          setOpenMergeQuestionModal={setOpenMergeQuestionModal}
        />
        <TaskHistory tasks={completedTasks} actions={actions} />
        <OtherAreasComponent upVotesByArea={questionState?.upvotes_by_area} />
      </div>
    </FullscreenPopup>
  );
}
